/**
 * Picture-in-picture (docs/live-breaks.md, "Storefront"; boxed's `pip-store`): the one
 * Twitch stream the store plays in its floating player, the stream pinned to the widget, where the
 * player sits and how wide it is, and whether the shopper hid the widget's stream row. Kept per tab
 * in sessionStorage, so a reload restores it and a new tab starts clean; never inside a popup (the
 * Twitch link's popup starts with a copy of its opener's sessionStorage, and would write back the
 * opener's stream as its own).
 *
 * The player itself (`components/liveBreaks/LiveBreaksDock.tsx`) is drawn once, beside the store's
 * frame, so navigating never remounts it; every surface that opens a stream (a live break's
 * toolbar, a directory card, the widget's View menu) goes through `openPip` here.
 */
import { parseTwitchLogin, twitchChannelUrl } from "@loot-os/api-contract";
import {
  LIVE_BREAK_PIP_INITIAL_LAYOUT,
  type LiveBreakPipLayout,
} from "@loot-os/design-system/domain/liveBreaks/pipGestures";
import { useSyncExternalStore } from "react";

/** What the player plays: one Twitch channel, and the live break it belongs to, if any. */
export interface WatchableStream {
  /** Canonical Twitch login. Two streams are the same stream when it matches. */
  channel: string;
  /** The host's name (the store's, for a house live break), else the channel's. */
  displayName: string;
  /** The channel's Twitch avatar, when Twitch has given one. */
  avatarUrl: string | null;
  /** The live break it streams; null for a promoted stream (a sponsored streamer with none). */
  liveBreakId: string | null;
}

/**
 * Where the player sits: moved from its resting corner, and its width once the shopper resized it
 * (null until then: the dock's width). The design system's frame reports both.
 */
export type PipLayout = LiveBreakPipLayout;

export interface PipState {
  /** Playing now; null while the player is closed. */
  stream: WatchableStream | null;
  /** The live break last opened this tab: the widget shows its row while it is open. */
  pinned: WatchableStream | null;
  layout: PipLayout;
  /** The shopper chose Hide widget; opening a stream shows the row again. */
  streamRowHidden: boolean;
  /** The promoted stream the shopper picked in the widget's stream picker, by channel. */
  selectedPromotedChannel: string | null;
}

export const INITIAL_PIP_LAYOUT: PipLayout = LIVE_BREAK_PIP_INITIAL_LAYOUT;

const EMPTY: PipState = {
  stream: null,
  pinned: null,
  layout: INITIAL_PIP_LAYOUT,
  streamRowHidden: false,
  selectedPromotedChannel: null,
};

/** sessionStorage's key; the version moves when the stored shape does. */
export const PIP_STORAGE_KEY = "loot-live-breaks-pip:v1";

// ---- reading what was stored ---------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A stored stream, or null when it is not one (an older shape, a hand-edited value). */
export function parseWatchableStream(raw: unknown): WatchableStream | null {
  if (!isRecord(raw)) return null;
  const channel = typeof raw.channel === "string" ? parseTwitchLogin(raw.channel) : null;
  if (!channel || typeof raw.displayName !== "string") return null;
  return {
    channel,
    displayName: raw.displayName || channel,
    avatarUrl: typeof raw.avatarUrl === "string" && raw.avatarUrl ? raw.avatarUrl : null,
    liveBreakId: typeof raw.liveBreakId === "string" && raw.liveBreakId ? raw.liveBreakId : null,
  };
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function parseLayout(raw: unknown): PipLayout {
  if (!isRecord(raw)) return INITIAL_PIP_LAYOUT;
  const offset = isRecord(raw.offset) ? raw.offset : {};
  const width = finite(raw.width);
  return {
    offset: { x: finite(offset.x) ?? 0, y: finite(offset.y) ?? 0 },
    width: width !== null && width > 0 ? width : null,
  };
}

/** What sessionStorage held, field by field: a field that drifted reads as its empty value. */
export function parsePipState(raw: unknown): PipState {
  if (!isRecord(raw)) return EMPTY;
  const selected =
    typeof raw.selectedPromotedChannel === "string"
      ? parseTwitchLogin(raw.selectedPromotedChannel)
      : null;
  return {
    stream: parseWatchableStream(raw.stream),
    pinned: parseWatchableStream(raw.pinned),
    layout: parseLayout(raw.layout),
    streamRowHidden: raw.streamRowHidden === true,
    selectedPromotedChannel: selected,
  };
}

/** The tab's sessionStorage, or none inside a popup or where storage is blocked. */
function tabStorage(): Storage | null {
  if (typeof window === "undefined" || window.opener) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function load(): PipState {
  const storage = tabStorage();
  if (!storage) return EMPTY;
  try {
    const raw = storage.getItem(PIP_STORAGE_KEY);
    return raw ? parsePipState(JSON.parse(raw)) : EMPTY;
  } catch {
    return EMPTY;
  }
}

// ---- the store ---------------------------------------------------------------------------------

let state: PipState = load();
const listeners = new Set<() => void>();

function set(next: PipState): void {
  if (next === state) return;
  state = next;
  try {
    tabStorage()?.setItem(PIP_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Full or blocked storage: the player lasts for this page only.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The PiP state now, outside React. */
export function getPipState(): PipState {
  return state;
}

/** The PiP state; re-renders on every change. */
export function usePipState(): PipState {
  return useSyncExternalStore(subscribe, getPipState, () => EMPTY);
}

/**
 * The stream playing now, alone: a page that only marks its PiP toggle reads this, so dragging
 * the player (a new layout on every move) never re-renders it.
 */
export function usePipStream(): WatchableStream | null {
  return useSyncExternalStore(
    subscribe,
    () => state.stream,
    () => null,
  );
}

/** The PiP state less where the player sits: what the widget's rows are decided from. */
export type PipRowsState = Omit<PipState, "layout">;

/**
 * The PiP state less the player's layout, so the widget's rows don't re-render while the shopper
 * drags the player; the player reads its layout itself.
 */
export function usePipRowsState(): PipRowsState {
  const stream = usePipStream();
  const pinned = useSyncExternalStore(
    subscribe,
    () => state.pinned,
    () => null,
  );
  const streamRowHidden = useSyncExternalStore(
    subscribe,
    () => state.streamRowHidden,
    () => false,
  );
  const selectedPromotedChannel = useSyncExternalStore(
    subscribe,
    () => state.selectedPromotedChannel,
    () => null,
  );
  return { stream, pinned, streamRowHidden, selectedPromotedChannel };
}

/** Whether `stream` plays the channel `login` names. */
export function isOnChannel(
  stream: Pick<WatchableStream, "channel"> | null | undefined,
  login: string | null | undefined,
): boolean {
  return Boolean(stream && login && stream.channel === parseTwitchLogin(login));
}

/**
 * Opens the player on `stream` (or swaps it in) and pins it, showing the widget's row again. A
 * live break's toolbar and a directory card keep the player where the shopper put it
 * (`keepLayout`); the widget's View menu opens it at its resting corner, as boxed's does.
 */
export function openPip(stream: WatchableStream, opts: { keepLayout?: boolean } = {}): void {
  const promoted = stream.liveBreakId === null;
  set({
    ...state,
    stream,
    // Only a live break has a row to pin; a promoted stream is the stream picker's pick instead.
    pinned: promoted ? null : stream,
    streamRowHidden: false,
    layout: opts.keepLayout ? state.layout : INITIAL_PIP_LAYOUT,
    selectedPromotedChannel: promoted ? stream.channel : null,
  });
  if (stream.liveBreakId !== null) recordRecentStream(stream);
}

/** Closes the player and keeps the pin, so the widget's row takes its place (the player's ✕). */
export function minimizePip(): void {
  if (state.stream === null && state.layout === INITIAL_PIP_LAYOUT) return;
  set({ ...state, stream: null, layout: INITIAL_PIP_LAYOUT });
}

/** Closes the player and drops the pin. */
export function closePip(): void {
  set({ ...state, stream: null, pinned: null, layout: INITIAL_PIP_LAYOUT });
}

/** Pins a live break's stream to the widget without playing it (the stream picker's Recent). */
export function pinStream(stream: WatchableStream): void {
  set({ ...state, pinned: stream, streamRowHidden: false, selectedPromotedChannel: null });
}

/** Drops the pin and leaves the player as it is (its live break closed or moved channel). */
export function unpinStream(): void {
  if (state.pinned === null) return;
  set({ ...state, pinned: null });
}

/** Picks a promoted stream for the widget's row (the stream picker), dropping the pin. */
export function selectPromotedStream(channel: string): void {
  set({ ...state, pinned: null, selectedPromotedChannel: parseTwitchLogin(channel) });
}

/** Hide widget: hides the stream row for this tab, until a stream opens again, and drops the pin. */
export function hideStreamRow(): void {
  set({ ...state, streamRowHidden: true, pinned: null });
}

/** Where the shopper moved or sized the player. */
export function setPipLayout(layout: Partial<PipLayout>): void {
  set({ ...state, layout: { ...state.layout, ...layout } });
}

/**
 * A live break's PiP toggle (the toolbar's, a directory card's): pressed while its channel plays;
 * pressing it plays this stream, keeping the player where it was, and pressing it again closes the
 * player as its ✕ does.
 */
export function pipToggle(
  stream: WatchableStream,
  playing: WatchableStream | null,
): { pressed: boolean; onPressedChange: (pressed: boolean) => void } {
  return {
    pressed: isOnChannel(playing, stream.channel),
    onPressedChange: (pressed) => {
      if (pressed) openPip(stream, { keepLayout: true });
      else minimizePip();
    },
  };
}

/**
 * Twitch's player for a channel, embedded on this page: `parent` must name the hostname the page
 * is served on, so it is read from the page, and a custom domain works as the store's subdomain
 * does. Muted, so it may start on its own.
 */
export function twitchPlayerUrl(channel: string, parent: string): string {
  const query = new URLSearchParams({ channel, parent, muted: "true", autoplay: "true" });
  return `https://player.twitch.tv/?${query}`;
}

/** The channel's page on Twitch, for Watch on Twitch. */
export function streamTwitchUrl(stream: Pick<WatchableStream, "channel">): string {
  return twitchChannelUrl(stream.channel);
}

// ---- recently watched live breaks ---------------------------------------------------------------

/** How many recently watched live breaks the stream picker lists (boxed's). */
export const MAX_RECENT_STREAMS = 5;

/** localStorage's key for them: per browser, unlike the player. */
export const RECENT_STREAMS_STORAGE_KEY = "loot-live-breaks-recent:v1";

function browserStorage(): Storage | null {
  if (typeof window === "undefined" || window.opener) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** A stored list of recent streams; anything that is not one is left out. */
export function parseRecentStreams(raw: unknown): WatchableStream[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .flatMap((item) => {
      const stream = parseWatchableStream(item);
      return stream?.liveBreakId ? [stream] : [];
    })
    .slice(0, MAX_RECENT_STREAMS);
}

function loadRecent(): WatchableStream[] {
  const storage = browserStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(RECENT_STREAMS_STORAGE_KEY);
    return raw ? parseRecentStreams(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

let recent: WatchableStream[] = loadRecent();
const recentListeners = new Set<() => void>();

function setRecent(next: WatchableStream[]): void {
  recent = next;
  try {
    browserStorage()?.setItem(RECENT_STREAMS_STORAGE_KEY, JSON.stringify(recent));
  } catch {
    // Blocked storage: remembered for this page only.
  }
  for (const listener of recentListeners) listener();
}

/** Puts a live break's stream first in the recent list (each live break once). */
export function recordRecentStream(stream: WatchableStream): void {
  if (stream.liveBreakId === null) return;
  setRecent(
    [stream, ...recent.filter((s) => s.liveBreakId !== stream.liveBreakId)].slice(
      0,
      MAX_RECENT_STREAMS,
    ),
  );
}

/** Drops live breaks that closed or are gone from the recent list. */
export function forgetRecentStreams(liveBreakIds: readonly string[]): void {
  if (liveBreakIds.length === 0) return;
  const next = recent.filter((s) => !liveBreakIds.includes(s.liveBreakId ?? ""));
  if (next.length !== recent.length) setRecent(next);
}

function subscribeRecent(listener: () => void): () => void {
  recentListeners.add(listener);
  return () => {
    recentListeners.delete(listener);
  };
}

const NO_RECENT: WatchableStream[] = [];

/** The live breaks watched in PiP in this browser, newest first. */
export function useRecentStreams(): WatchableStream[] {
  return useSyncExternalStore(
    subscribeRecent,
    () => recent,
    () => NO_RECENT,
  );
}

/** Test seam: forget every stream and the stored state, as a new tab would. */
export function resetPipForTests(): void {
  state = EMPTY;
  recent = [];
  for (const listener of listeners) listener();
  for (const listener of recentListeners) listener();
}
